import { useQuery } from "react-query";
import { ReservationService } from "../../services/ReservationService";
import { Card, Col, Collapse, CollapseProps, Flex, Input, Row, Skeleton, Space } from "antd";
import { Fragment, useEffect, useState } from "react";
import { mdlReservationDiffPairItem } from "../../models/service-models/reservation/ReservationDiffPairItem";
import _ from "lodash";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { mdlReservation } from "../../models/domain/Reservation";
import { mldReservationDiffPairHolder } from "../../models/service-models/reservation/ReservationDiffPairHolder";
import { mdlCustomer } from "../../models/domain/Customer";
import { mdlReservationExtraService } from "../../models/domain/ReservationExtraService";
import { enmQuotaType } from "../../models/enums/QuotaType";
import { InputHelper } from "../../utils/InputHelper";
import { enmReservationType } from "../../models/enums/ReservationType";
import { enmReservationStatus } from "../../models/enums/ReservationStatus";
import { enmConfirmationStatus } from "../../models/enums/ConfirmationStatus";

type ReservationHistoryProps = {
    reservationId?: string;
}

class ResLogViewItem {
    label?: string;
    key?: string;
    differences?: Array<mdlReservationDiffPairItem>;
    reservation?: mdlReservation;
}

export const ReservationHistory = (props: ReservationHistoryProps) => {
    const [logViewData, setLogViewData] = useState<Array<ResLogViewItem>>();
    const { t } = useTranslation();

    const { data, isLoading, refetch, isError } = useQuery(
        ["getReservationHistory", props.reservationId],
        () => {
            if (props.reservationId && !logViewData)
                return ReservationService.History({ reservationId: props.reservationId });
        },
        {
            onSuccess(data) {
                if (data && data.header && data.header.success && data.body && data.body.baseFile && data.body.diffPairs) {
                    var list = new Array<ResLogViewItem>();
                    list.push({ key: "base", label: `${t("history.origin")} (${dayjs(data.body.baseFile.logDate, "YYYY-MM-DDTHH:mm:ss").format("DD.MM.YYYY HH:mm:ss")})`, reservation: data.body.baseFile })
                    _.each(data.body.diffPairs, function (d: mldReservationDiffPairHolder) {
                        var item = new ResLogViewItem();
                        item.label = `${dayjs(d.changeDate, "YYYY-MM-DDTHH:mm:ss").format("DD.MM.YYYY HH:mm:ss")}`;
                        item.key = item.label;
                        item.reservation = d.reservation;
                        item.differences = d.differences;
                        list.push(item);
                    });
                    setLogViewData(list);
                }
            },
            staleTime: 0,
            cacheTime: 0,
            refetchOnWindowFocus: false,
            enabled: !!props.reservationId
        }
    );

    const getQuotaTypeText = (quotaType?: enmQuotaType) => {
        if (quotaType != null) {
            var quotaText = enmQuotaType[quotaType];
            return t(`reservationDetail.quotaType${InputHelper.capitalizeFirstLetter(quotaText.toLowerCase())}`)
        }
        return "-";
    }

    const getResConfType = (confirmationStatus?: enmConfirmationStatus) => {
        switch (Number(confirmationStatus)) {
            case enmConfirmationStatus.Wait:
                return t('resTable.confirmationStatusWait');
            case enmConfirmationStatus.Yes:
                return t('resTable.confirmationStatusYes');
            case enmConfirmationStatus.No:
                return t('resTable.confirmationStatusNo');
            case enmConfirmationStatus.Sent:
                return t('resTable.confirmationStatusSent');
            case enmConfirmationStatus.NotSent:
                return t('resTable.confirmationStatusNotSent');
        }
    }

    const getResTypeText = (reservationType?: enmReservationType) => {
        if (reservationType != null) {
            var resTypeText = enmReservationType[reservationType];
            return t(`reservationDetail.resType${InputHelper.capitalizeFirstLetter(resTypeText.toLowerCase())}`)
        }
        return "-";
    }

    const getResStatusText = (reservationStatus?: enmReservationStatus) => {
        if (reservationStatus != null) {
            var resStatusText = enmReservationStatus[reservationStatus];
            return t(`reservationDetail.resStatus${InputHelper.capitalizeFirstLetter(resStatusText.toLowerCase())}`)
        }
        return "-";
    }


    const getValue = (path: string, value: any) => {
        switch (path) {
            case "history.resExtra.data":
                if (!InputHelper.isNullOrUndefinedOrEmpty(value)) {
                    const parsed = typeof value === "string" ? JSON.parse(value) : value;

                    const description = parsed?.Description ?? "-";
                    const price = parsed?.Price ?? "-";
                    const currency = parsed?.Currency ?? "";

                    return `${description} - ${price} ${currency}`;
                }
                else {
                    return "-";
                }
            case "history.resHotel.confirmationStatus":
                return getResConfType(value);
            case "history.resHotel.reservationType":
                return getResTypeText(value);
            case "history.resHotel.quotaType":
                return getQuotaTypeText(value);
            case "history.resHotel.resStatus":
                return getResStatusText(value);
            case "history.resHotel.checkIn":
            case "history.resHotel.checkOut":
            case "history.resHotel.sellDate":
            case "history.resHotel.confirmDate":
                if (!InputHelper.isNullOrUndefinedOrEmpty(value))
                    return dayjs(value, "MM-DD-YYYY").format("DD.MM.YYYY");
                else
                    return "-";
            default:
                if (InputHelper.isNullOrUndefinedOrEmpty(value)) {
                    return "-";
                }
                return value;
        }
    }
    const renderDiffView = (item?: ResLogViewItem) => {
        return (
            <Fragment>
                {
                    <>
                        {
                            renderBaseItem(item?.reservation, true)
                        }
                        <Card>
                            <Row gutter={16}>
                                {
                                    item?.differences?.map((diff: mdlReservationDiffPairItem, index: number) => (

                                        <Col key={index} span={6}>
                                            <Flex vertical gap={10}>
                                                <strong>{t(diff.path!)}</strong>
                                                <Space style={{ flexWrap: "wrap" }}>
                                                    <span
                                                        style={{ color: "red", textDecoration: "line-through" }}>{getValue(diff.path!, diff.oldValue!)}</span>
                                                    ➤
                                                    <span
                                                        style={{ color: "green" }}>{getValue(diff.path!, diff.newValue!)}</span>
                                                </Space>

                                            </Flex>
                                        </Col>

                                    ))
                                }
                            </Row>
                            <Flex justify="center" align="center" gap={30} style={{ marginTop: 10, borderRadius: 10, border: "1px solid rgb(255 255 255 / 90%)", backgroundColor: "#f7f7f7", boxShadow: "inset 0px 0px 3px 2px rgb(185 184 184 / 75%)" }}>
                                <span><strong>{t("history.base.lastChangeUser")} : </strong> {item?.reservation?.lastChangeUser} </span>
                                {
                                    item?.reservation?.confUser
                                    &&
                                    <span><strong>{t("history.base.confUser")} :</strong>  {item?.reservation?.confUser} </span>
                                }
                            </Flex>
                        </Card>
                    </>

                }
            </Fragment>
        )
    }


    const renderBaseItem = (reservation?: mdlReservation, fromDiff?: boolean) => {
        return (
            <Fragment>
                {
                    reservation
                    &&
                    <Card>
                        <Row gutter={4} justify={"center"}>
                            <Col span={6}>
                                <span>{t("history.base.voucher")} : {reservation.voucherNo}</span>

                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.resStatus")} : {getResStatusText(reservation.reservationStatus)}</span>

                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.resType")} : {getResTypeText(reservation.reservationType)}</span>

                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.saleDate")} : {dayjs(reservation.sellDate, "YYYY-MM-DD").format("DD.MM.YYYY")}</span>
                            </Col>
                        </Row>

                        <Row gutter={4} justify={"center"}>
                            <Col span={6}>
                                <span>{t("history.base.checkInDate")} : {dayjs(reservation.checkIn, "YYYY-MM-DD").format("DD.MM.YYYY")}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.checkOutDate")} : {dayjs(reservation.checkOut, "YYYY-MM-DD").format("DD.MM.YYYY")}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.night")} : {reservation.nights}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.accomType")} : {reservation.roomName}</span>
                            </Col>
                        </Row>

                        <Row gutter={4} justify={"center"}>
                            <Col span={6}>
                                <span>{t("history.base.roomType")} : {reservation.roomType}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.board")} : {reservation.accomName}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.roomCount")} : {reservation.roomCount}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.quotaType")} : {getQuotaTypeText(reservation.quotaType)}</span>
                            </Col>
                        </Row>

                        <Row gutter={4} justify={"center"}>
                            <Col span={6}>
                                <span>{t("history.base.price")} : {reservation.soldPrice}  {reservation.soldCurrency}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.adult")} : {reservation.adults}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.child")} : {reservation.childs}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.infant")} : {reservation.infants}</span>
                            </Col>
                        </Row>

                        <Row gutter={4} justify={"center"}>
                            <Col span={6}>
                                <span>{t("history.base.extraBed")} : {reservation.extraBed}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.confirmNo")} : {reservation.confirmationNo}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.note")} : {reservation.note}</span>
                            </Col>
                            <Col span={6}>
                                <span>{t("history.base.confirmNote")} : {reservation.confirmeNote}</span>
                            </Col>
                        </Row>
                        <br />
                        <br />
                        <Row justify={"center"}>
                            <Fragment>
                                {
                                    reservation.customers?.map((customer: mdlCustomer, custIndex: number) => (
                                        <Fragment key={custIndex}>
                                            <Col span={6}>
                                                <span>{t("history.base.customerName")} : {customer.title} {customer.name}</span>
                                            </Col>
                                            <Col span={6}>
                                                <span>{t("history.base.birthDate")} : {customer.birthDate && dayjs(customer.birthDate) > dayjs("1902-01-01") ? dayjs(customer.birthDate, ("YYYY-MM-DD")).format("DD.MM.YYYY") : "-"}</span>
                                            </Col>
                                            <Col span={6}>
                                                <span>{t("history.base.nationality")} : {customer.nationality}</span>

                                            </Col>
                                            <Col span={6}>
                                                <span>{t("history.base.age")} : {customer.age ? customer.age : "-"}</span>

                                            </Col>
                                        </Fragment>
                                    ))
                                }
                            </Fragment>
                        </Row>
                        <Row justify={"center"}>
                            <Fragment>
                                {
                                    reservation.extraServices?.map((extra: mdlReservationExtraService, extIndex: number) => (
                                        <Fragment key={extIndex}>
                                            <Col span={5}>
                                                <span>{t("history.base.serviceCode")} : {extra.code} </span>
                                            </Col>
                                            <Col span={5}>
                                                <span>{t("history.base.serviceDesc")} : {extra.description} </span>
                                            </Col>
                                            <Col span={5}>
                                                <span>{t("history.base.servicePax")} : {extra.pax} </span>

                                            </Col>
                                            <Col span={5}>
                                                <span>{t("history.base.servicePax")} : {extra.pax} </span>

                                            </Col>
                                            <Col span={4}>
                                                <span>{t("history.base.servicePrice")} : {extra.price} </span>

                                            </Col>
                                        </Fragment>
                                    ))
                                }
                            </Fragment>
                        </Row>

                        {
                            !fromDiff
                            &&
                            <Flex justify="center" align="center" gap={30} style={{ marginTop: 10, borderRadius: 10, border: "1px solid rgb(255 255 255 / 90%)", backgroundColor: "#f7f7f7", boxShadow: "inset 0px 0px 3px 2px rgb(185 184 184 / 75%)" }}>
                                <span><strong>{t("history.base.creator")} : </strong> {reservation?.lastChangeUser} </span>

                            </Flex>
                        }
                    </Card>
                }
            </Fragment >
        )
    }



    const items: CollapseProps['items'] = logViewData?.map((item, index) =>
    ({
        key: `${item.key}-${index}`,
        label: item.label,
        children: item.key == 'base' ? renderBaseItem(item.reservation) : renderDiffView(item),
    })
    );




    return (
        <>
            <Card loading={isLoading}>
                {
                    data && data.body && data.body.diffPairs
                    &&
                    <Collapse accordion items={items} />
                }
            </Card>
            {
                isLoading
                &&
                <>
                    <Skeleton active={true} />
                    <Skeleton active={true} />
                    <Skeleton active={true} />
                </>
            }
        </>
    )
}